import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {take} from 'rxjs/operators';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BenefitDTO} from '../../../models/BenefitDTO';
import {BenefitService} from '../../../services/benefit.service';
import {SharedService} from '../../../../common/services/shared-service';
import {LookupService} from '../../../../common/services/lookup.service';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {BenefitGenderLinkDTO} from '../../../models/BenefitGenderLinkDTO';
import {BenefitEncounterTypeLinkDTO} from '../../../models/BenefitEncounterTypeLinkDTO';
import {CustomValidator} from '../../../../common';

enum Controls {
  AgeMin = 'ageMin',
  AgeMax = 'ageMax',
  BenefitId = 'benefitId',
  BenefitCode = 'benefitCode',
  EncounterType = 'encounterType',
  FamilyOfBenefit = 'familyOfBenefit',
  Gender = 'gender',
  Name = 'name',
  BookBenefit = 'bookBenefit'
}

@Component({
  selector : 'app-add-benefit',
  templateUrl : './add-benefit-dialog.component.html',
  styleUrls : [
    '../../../../../../styles/global/_add-view-edit-styles.scss',
    './add-benefit-dialog.component.scss'
  ]
})
export class AddBenefitDialogComponent implements OnInit {
  readonly controls = Controls;
  readonly lookupValue = LookupValue;

  errorMessage = '';
  heading = 'Add Benefit';
  benefitDTO: BenefitDTO;
  benefitFormGroup: FormGroup;
  isEditMode: boolean;
  familyOfBenefitLookupValues: NameValueDTO[];
  processingSubClassValues: NameValueDTO[];
  encounterTypeLookupValues: NameValueDTO[];
  genderLookupValues: NameValueDTO[];
  encounterTypesList: BenefitEncounterTypeLinkDTO[] = [];
  gendersList: BenefitGenderLinkDTO[] = [];
  selectedProcessingSubClass: string;

  private readonly loggedInUserName: string;

  constructor(@Inject(MAT_DIALOG_DATA) public data: {benefitId: bigint},
              private benefitService: BenefitService,
              public dialogRef: MatDialogRef<AddBenefitDialogComponent>,
              private formBuilder: FormBuilder,
              private lookupService: LookupService,
              private sharedService: SharedService) {

    this.loggedInUserName = this.sharedService.getLoggedInUserName();
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.initLookUpValues();
    this.getHelpInfo('Benefit');

    const {benefitId} = this.data;
    if (benefitId) {
      this.isEditMode = true;
      this.heading = 'Edit Benefit';
      this.getBenefitByBenefitId(benefitId);
    }
  }

  initFormGroup(): void {
    this.benefitFormGroup = this.formBuilder.group({
      [Controls.BenefitId]: new FormControl(),
      [Controls.Name]: new FormControl(null, [Validators.required]),
      [Controls.BenefitCode]: new FormControl(null, [Validators.required]),
      [Controls.AgeMin]: new FormControl(null, [CustomValidator.numeric, CustomValidator.wholeNumber]),
      [Controls.AgeMax]: new FormControl(null, [CustomValidator.numeric, CustomValidator.wholeNumber]),
      [Controls.FamilyOfBenefit]: new FormControl(null),
      [Controls.Gender]: new FormControl(), // Ideally: Single gender at a time
      [Controls.EncounterType]: new FormControl(), // Ideally: Single Encounter Type at a time
      [Controls.BookBenefit]: new FormControl(false)
    });
  }

  initLookUpValues(): void {
    this.getListOfLookupValues(this.lookupValue.EncounterType);
    this.getListOfLookupValues(this.lookupValue.Gender);
    this.getListOfLookupValues(this.lookupValue.FamilyOfBenefit);
    this.getListOfLookupValues(this.lookupValue.ProcessingSubClass);
  }

  getHelpInfo(componentName: string): void {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onUpdateGendersList(): void {
    const genderId = this.benefitFormGroup.get('gender').value;
    const isGenderFound = this.findBenefitGenderLinkDTOInList(genderId);

    if (isGenderFound) {
      this.sharedService.openSnackBar(`${isGenderFound.gender.name} has already been selected`, 'Dismiss');
      this.benefitFormGroup.get('gender')
          .reset();
    }
    else {
      const newBenefitGender = this.getBenefitGenderLinkDTO(genderId);
      this.gendersList.push(newBenefitGender);
      this.benefitFormGroup.get('gender')
          .reset();
      this.errorMessage = null;
    }
  }

  onUpdateEncounterTypesList(): void {
    const encounterTypeId = this.benefitFormGroup.get('encounterType').value;
    const isBenefitEncounterTypeFound = this.findBenefitEncounterTypeLinkDTOInList(encounterTypeId);

    if (isBenefitEncounterTypeFound) {
      this.sharedService.openSnackBar(`${isBenefitEncounterTypeFound.encounterType.type} has already been selected`, 'Dismiss');
      this.benefitFormGroup.get('encounterType')
          .reset();
    }
    else {
      const newBenefitGenderLinkDTO = this.getBenefitEncounterTypeLinkDTO(encounterTypeId);
      this.encounterTypesList.push(newBenefitGenderLinkDTO);
      this.benefitFormGroup.get('encounterType')
          .reset();
      this.errorMessage = null;
    }
  }

  onSaveBenefit(): void {
    if (this.encounterTypesList && this.encounterTypesList.length === 0) {
      this.errorMessage = 'At least 1 encounter type must be selected.';
      return;
    }

    if (this.gendersList && this.gendersList.length === 0) {
      this.errorMessage = 'At least 1 gender must be selected.';
      return;
    }

    const benefitDTOPayload: BenefitDTO = this.getBenefitDTOPayload();
    this.benefitService.saveBenefit(benefitDTOPayload)
        .pipe(take(1))
        .subscribe({
          next: (response: BenefitDTO | any) => {
              if (response) {
                this.sharedService.openSnackBar(`Benefit ${this.isEditMode ? 'edited' : 'added'} successfully`, 'Dismiss');
                this.dialogRef.close({isSaved: true});
                this.resetState();
              }
              else if (response && response.businessMessage) {
                this.sharedService.openSnackBar(`${response.businessMessage}`, 'Dismiss');
              }
          },
          error: (error) => {
            console.error('Failed to save benefit', error);
            this.sharedService.openErrorDialog(error.message);
          }
        });
  }

  onDeleteEncounterType(item: BenefitEncounterTypeLinkDTO): void {
    const name = item.encounterType?.type;
    const index = this.encounterTypesList.findIndex((encounterTypeItem: BenefitEncounterTypeLinkDTO) => {
      return encounterTypeItem.encounterType.type === name;
    });
    if (index !== -1) {
      this.encounterTypesList.splice(index, 1);
    }
  }

  onDeleteGender(item: BenefitGenderLinkDTO): void {
    const name = item.gender?.name;
    const index = this.gendersList.findIndex((genderItem: BenefitGenderLinkDTO) => {
      return genderItem.gender.name === name;
    });
    if (index !== -1) {
      this.gendersList.splice(index, 1);
    }
  }

  onProcessingSubClassSelectionChanged(event: string): void {
    this.selectedProcessingSubClass = event;
  }

  private getBenefitByBenefitId(benefitId: bigint): void {
    this.benefitService.getBenefitByBenefitId(benefitId)
        .pipe(
          take(1)
        )
        .subscribe({
          next: (response: BenefitDTO) => {
            if (response) {
              this.benefitDTO = response;
              this.setFormsForEdit(response);
            }
            else {
              this.sharedService.openSnackBar(`Benefit by ${benefitId} not found`, 'Dismiss');
            }
          },
          error: (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        });
  }

  private setFormsForEdit(data: BenefitDTO): void {
    this.setBenefitFormForEdit(data);
  }

  private setBenefitFormForEdit(benefit: BenefitDTO): void {
    const {benefitId, name, benefitCode, ageMin, ageMax, genders, encounterTypes, familyOfBenefit, bookBenefit} = benefit;
    this.heading = `Edit Benefit - ${name}`;
    this.benefitFormGroup.patchValue({
      benefitId,
      name,
      benefitCode,
      ageMin,
      ageMax,
      familyOfBenefit : this.familyOfBenefitLookupValues.find(item => item.value === familyOfBenefit?.value)?.value,
      bookBenefit : bookBenefit === 1
    });

    if (genders && genders.length > 0) {
      this.gendersList = genders;
    }

    if (encounterTypes && encounterTypes.length > 0) {
      this.encounterTypesList = encounterTypes;
    }
    this.selectedProcessingSubClass = this.benefitDTO.processingSubClass?.name;
  }

  private getBenefitGenderLinkDTO(genderId: number): BenefitGenderLinkDTO {
    let payload: BenefitGenderLinkDTO;
    const genderDetails: NameValueDTO = this.getGenderByGenderId(genderId);
    const isGenderFound: BenefitGenderLinkDTO = this.findBenefitGenderLinkDTOInList(genderId);

    payload = {
      gender : {
        genderId : genderDetails.value,
        name : genderDetails.name,
        created : (this.isEditMode && isGenderFound) ? isGenderFound.gender.created : new Date(),
        createdBy : (this.isEditMode && isGenderFound) ? isGenderFound.gender.createdBy : this.loggedInUserName,
        tenantId : (this.isEditMode && isGenderFound) ? isGenderFound.gender.tenantId : null,
        updated : new Date(),
        updatedBy : this.loggedInUserName
      },
      created : (this.isEditMode && isGenderFound) ? isGenderFound.created : new Date(),
      createdBy : (this.isEditMode && isGenderFound) ? isGenderFound.createdBy : this.loggedInUserName,
      tenantId : (this.isEditMode && isGenderFound) ? isGenderFound.tenantId : null,
      updated : new Date(),
      updatedBy : this.loggedInUserName
    };
    return payload;
  }

  private getBenefitEncounterTypeLinkDTO(encounterTypeId: bigint) {
    let payload: any;
    const encounterTypeDetails = this.getBenefitEncounterTypeBy(encounterTypeId);
    const isBenefitEncounterTypeFound = this.findBenefitEncounterTypeLinkDTOInList(encounterTypeId);

    payload = {
      encounterType : {
        encounterTypeId : encounterTypeDetails.value,
        code : null,
        type : encounterTypeDetails.name,
        processingClass : null,
        created : (this.isEditMode && isBenefitEncounterTypeFound) ? isBenefitEncounterTypeFound.encounterType.created : new Date(),
        createdBy : (this.isEditMode && isBenefitEncounterTypeFound) ? isBenefitEncounterTypeFound.encounterType.createdBy : this.loggedInUserName,
        tenantId : (this.isEditMode && isBenefitEncounterTypeFound) ? isBenefitEncounterTypeFound.encounterType.tenantId : null,
        updated : new Date(),
        updatedBy : this.loggedInUserName
      },
      created : (this.isEditMode && isBenefitEncounterTypeFound) ? isBenefitEncounterTypeFound.created : new Date(),
      createdBy : (this.isEditMode && isBenefitEncounterTypeFound) ? isBenefitEncounterTypeFound.createdBy : this.loggedInUserName,
      tenantId : (this.isEditMode && isBenefitEncounterTypeFound) ? isBenefitEncounterTypeFound.tenantId : null,
      updated : new Date(),
      updatedBy : this.loggedInUserName
    };

    return payload;
  }

  private getBenefitDTOPayload(): BenefitDTO {
    let payload: BenefitDTO;
    const familyOfBenefitValue = this.benefitFormGroup.get(Controls.FamilyOfBenefit).value;
    payload = {
      benefitId : this.isEditMode ? this.benefitDTO.benefitId : null,
      name : this.benefitFormGroup.get(Controls.Name).value,
      benefitCode : this.benefitFormGroup.get(Controls.BenefitCode).value,
      ageMin : this.benefitFormGroup.get(Controls.AgeMin).value,
      ageMax : this.benefitFormGroup.get(Controls.AgeMax).value,
      familyOfBenefit : this.familyOfBenefitLookupValues.find(item => item.value === familyOfBenefitValue),
      genders : this.gendersList,
      encounterTypes : this.encounterTypesList,
      bookBenefit : this.benefitFormGroup.get(Controls.BookBenefit).value ? 1 : 0,
      created : this.isEditMode ? this.benefitDTO.created : new Date(),
      createdBy : this.isEditMode ? this.benefitDTO.createdBy : this.loggedInUserName,
      tenantId : this.isEditMode ? this.benefitDTO.tenantId : null,
      updated : this.isEditMode ? new Date() : null,
      updatedBy : this.isEditMode ? this.loggedInUserName : null,
      processingSubClass: this.processingSubClassValues.find(item => item.name === this.selectedProcessingSubClass)
    };

    return payload;
  }

  private getGenderByGenderId(genderId: number): NameValueDTO {
    return this.genderLookupValues.find((gender: NameValueDTO) => {
      return gender.value === genderId;
    });
  }

  private findBenefitGenderLinkDTOInList(genderId: number): BenefitGenderLinkDTO {
    return this.gendersList.find((item: BenefitGenderLinkDTO) => {
      return item.gender.genderId === genderId;
    });
  }

  private getBenefitEncounterTypeBy(encounterTypeId: bigint): NameValueDTO {
    return this.encounterTypeLookupValues.find((encounter: NameValueDTO) => {
      // Explicit type cast to "number" as NameValueDTO type matches to number
      return encounter.value === Number(encounterTypeId);
    });
  }

  private findBenefitEncounterTypeLinkDTOInList(encounterTypeId: bigint): BenefitEncounterTypeLinkDTO {
    return this.encounterTypesList.find((encounter: BenefitEncounterTypeLinkDTO) => {
      return encounter.encounterType.encounterTypeId === encounterTypeId;
    });
  }

  private getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity)
        .pipe(
          take(1)
        )
        .subscribe({
            next: (response: NameValueDTO[]) => {
              if (entity === this.lookupValue.EncounterType) {
                this.encounterTypeLookupValues = response;
              }
              else if (entity === this.lookupValue.Gender) {
                this.genderLookupValues = response;
              }
              else if (entity === this.lookupValue.FamilyOfBenefit) {
                this.familyOfBenefitLookupValues = response;
              }
              else if (entity === this.lookupValue.ProcessingSubClass) {
                this.processingSubClassValues = response;
              }
          },
          error: errorResponse => {
            const error = errorResponse.error;
            this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
          }
        });
  }

  private resetState(): void {
    this.benefitDTO = null;
    this.encounterTypesList = [];
    this.gendersList = [];

    // Reset FormGroup/Control values:
    this.benefitFormGroup.reset();
  }
}
